import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Form, Input, Button, ConfigProvider, Select, AutoComplete, message } from 'antd';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { API_BASE_URL } from '../../config';
import { paginateOrders } from '../../utils/adminOrderTable.mjs';
import '../AdminDashboard.css';
import './InventoryTab.css';

const PAGE_SIZE = 25;
const LOW_STOCK_KEY = 'inventoryLowStockThreshold';
const THEME = { token: { colorPrimary: '#c41e3a', borderRadius: 8 } };

const SUB_TABS = [
    { key: 'purchases', label: 'Purchase History' },
    { key: 'stock', label: 'Stock Levels' },
    { key: 'movements', label: 'Stock Movements' },
    { key: 'suppliers', label: 'Suppliers' },
];

const PAYMENT_METHODS = ['Cash', 'PayNow', 'Bank Transfer', 'Credit Card', 'Other'];
const ADJUST_REASONS = ['Stock count correction', 'Damaged', 'Lost', 'Returned to supplier', 'Personal / shop use', 'Other'];

const STATUS_STYLES = {
    received: { background: '#d4edda', color: '#155724', label: 'Received' },
    ordered: { background: '#fff3cd', color: '#856404', label: 'Ordered' },
    void: { background: '#e2e3e5', color: '#383d41', label: 'Void' },
    paid: { background: '#d4edda', color: '#155724', label: 'Paid' },
    unpaid: { background: '#f8d7da', color: '#721c24', label: 'Unpaid' },
};

const MOVEMENT_LABELS = {
    purchase: { label: 'Purchase', background: '#d4edda', color: '#155724' },
    purchase_void: { label: 'Purchase void', background: '#e2e3e5', color: '#383d41' },
    adjustment: { label: 'Adjustment', background: '#d1ecf1', color: '#0c5460' },
    string_order: { label: 'String order', background: '#fde2e4', color: '#8a1c2b' },
    string_order_cancel: { label: 'Order cancelled', background: '#fff3cd', color: '#856404' },
};

const todayStr = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const num = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
};
const money = (v) => `SGD ${num(v).toFixed(2)}`;
const qty = (v) => {
    const n = num(v);
    return Number.isInteger(n) ? String(n) : n.toFixed(2);
};
const formatDateTime = (v) => (v ? new Date(v).toLocaleString('en-SG', { dateStyle: 'medium', timeStyle: 'short' }) : '—');
const monthLabel = (ym) => {
    const [y, m] = ym.split('-').map(Number);
    return new Date(y, m - 1, 1).toLocaleString('en-SG', { month: 'short', year: '2-digit' });
};

const newLine = (item = null) => ({
    key: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
    item_id: item?.item_id || undefined,
    quantity: '1',
    unit_cost: item?.last_unit_cost != null ? num(item.last_unit_cost).toFixed(2) : '',
});

const emptyPurchaseForm = () => ({
    supplier_name: '',
    purchase_date: todayStr(),
    reference_no: '',
    status: 'received',
    shipping_fee: '',
    discount: '',
    payment_status: 'unpaid',
    payment_method: undefined,
    notes: '',
    lines: [newLine()],
});

const EMPTY_SUPPLIER = { name: '', contact_person: '', phone: '', email: '', notes: '' };

function Pill({ kind, map = STATUS_STYLES }) {
    const s = map[kind] || { background: '#e2e3e5', color: '#383d41', label: kind || '—' };
    return <span className="order-status-pill" style={{ background: s.background, color: s.color }}>{s.label}</span>;
}

function ErrorBox({ children }) {
    if (!children) return null;
    return <div className="inv-error">{children}</div>;
}

function Modal({ title, onClose, width = 480, children }) {
    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content inv-modal" style={{ maxWidth: width }} onClick={e => e.stopPropagation()}>
                <button className="modal-close" onClick={onClose}>×</button>
                <h3 style={{ marginBottom: 16 }}>{title}</h3>
                <ConfigProvider theme={THEME}>{children}</ConfigProvider>
            </div>
        </div>
    );
}

function Pagination({ paginated, onPage, label }) {
    return (
        <div className="table-pagination">
            <span className="table-pagination-summary">
                Showing {paginated.items.length} of {paginated.totalItems} {label}
            </span>
            <div className="table-pagination-controls">
                <button type="button" className="btn-cancel table-page-btn" onClick={() => onPage(p => Math.max(1, p - 1))} disabled={paginated.currentPage === 1}>Previous</button>
                <span className="table-page-indicator">Page {paginated.currentPage} of {paginated.totalPages}</span>
                <button type="button" className="btn-save table-page-btn" onClick={() => onPage(p => Math.min(paginated.totalPages, p + 1))} disabled={paginated.currentPage === paginated.totalPages}>Next</button>
            </div>
        </div>
    );
}

function downloadCsv(filename, rows) {
    const escape = (v) => {
        const s = v === null || v === undefined ? '' : String(v);
        return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const csv = rows.map(r => r.map(escape).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
}

function InventoryTab() {
    const navigate = useNavigate();
    const token = localStorage.getItem('adminToken');

    const authHeaders = useCallback(() => ({
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
    }), [token]);

    const handleAuthError = useCallback((res) => {
        if (res.status === 401) {
            localStorage.removeItem('adminToken');
            localStorage.removeItem('adminUsername');
            navigate('/admin');
            return true;
        }
        return false;
    }, [navigate]);

    // Small wrapper so every call shares auth + error handling.
    const api = useCallback(async (path, options = {}) => {
        const res = await fetch(`${API_BASE_URL}/admin/inventory${path}`, { headers: authHeaders(), ...options });
        if (handleAuthError(res)) throw new Error('Session expired');
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.error || 'Request failed');
        return data;
    }, [authHeaders, handleAuthError]);

    const [subTab, setSubTab] = useState('purchases');
    const [loading, setLoading] = useState(false);
    const [summary, setSummary] = useState({ totals: {}, monthly: [] });
    const [purchases, setPurchases] = useState([]);
    const [stockItems, setStockItems] = useState([]);
    const [suppliers, setSuppliers] = useState([]);
    const [movements, setMovements] = useState([]);

    const [lowStockThreshold, setLowStockThreshold] = useState(() => {
        try {
            const v = localStorage.getItem(LOW_STOCK_KEY);
            return v !== null ? Number(v) : 3;
        } catch {
            return 3;
        }
    });
    useEffect(() => {
        try { localStorage.setItem(LOW_STOCK_KEY, String(lowStockThreshold)); } catch { /* ignore */ }
    }, [lowStockThreshold]);

    const refreshAll = useCallback(async () => {
        setLoading(true);
        try {
            const [s, p, st, sup] = await Promise.all([
                api('/summary'), api('/purchases'), api('/stock'), api('/suppliers'),
            ]);
            setSummary(s);
            setPurchases(p.purchases || []);
            setStockItems(st.items || []);
            setSuppliers(sup.suppliers || []);
        } catch (err) {
            if (err.message !== 'Session expired') message.error(`Failed to load inventory: ${err.message}`);
        } finally {
            setLoading(false);
        }
    }, [api]);

    const fetchMovements = useCallback(async () => {
        try {
            const data = await api('/movements?limit=1000');
            setMovements(data.movements || []);
        } catch (err) {
            if (err.message !== 'Session expired') message.error(err.message);
        }
    }, [api]);

    useEffect(() => { refreshAll(); }, [refreshAll]);
    useEffect(() => { if (subTab === 'movements') fetchMovements(); }, [subTab, fetchMovements]);

    const refreshAfterChange = () => {
        refreshAll();
        if (subTab === 'movements') fetchMovements();
    };

    const itemsById = useMemo(() => Object.fromEntries(stockItems.map(i => [i.item_id, i])), [stockItems]);
    const purchasableItems = useMemo(
        () => stockItems.filter(i => i.item_type !== 'service' && i.status !== 'inactive'),
        [stockItems]
    );
    const lowStockItems = useMemo(
        () => purchasableItems.filter(i => i.stock_on_hand !== null && num(i.stock_on_hand) <= lowStockThreshold),
        [purchasableItems, lowStockThreshold]
    );

    // ── Monthly spend chart data (always 12 bars, empty months = 0) ──────────
    const chartData = useMemo(() => {
        const byMonth = Object.fromEntries((summary.monthly || []).map(m => [m.month, m]));
        const now = new Date();
        return Array.from({ length: 12 }, (_, idx) => {
            const d = new Date(now.getFullYear(), now.getMonth() - 11 + idx, 1);
            const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
            return { month: key, label: monthLabel(key), total: num(byMonth[key]?.total), count: num(byMonth[key]?.count) };
        });
    }, [summary.monthly]);

    // ── Purchase history filters ──────────────────────────────────────────────
    const [pSearch, setPSearch] = useState('');
    const [pFrom, setPFrom] = useState('');
    const [pTo, setPTo] = useState('');
    const [pSupplier, setPSupplier] = useState();
    const [pStatus, setPStatus] = useState();
    const [pPayment, setPPayment] = useState();
    const [pPage, setPPage] = useState(1);

    const filteredPurchases = useMemo(() => {
        const q = pSearch.trim().toLowerCase();
        return purchases.filter(p => {
            if (pFrom && p.purchase_date < pFrom) return false;
            if (pTo && p.purchase_date > pTo) return false;
            if (pSupplier && p.supplier_id !== pSupplier) return false;
            if (pStatus && p.status !== pStatus) return false;
            if (pPayment && p.payment_status !== pPayment) return false;
            if (!q) return true;
            return [p.purchase_no, p.reference_no, p.supplier_name, p.notes, ...p.items.map(i => i.item_name)]
                .some(v => v && String(v).toLowerCase().includes(q));
        });
    }, [purchases, pSearch, pFrom, pTo, pSupplier, pStatus, pPayment]);

    const filteredSpend = useMemo(
        () => filteredPurchases.filter(p => p.status !== 'void').reduce((s, p) => s + num(p.total_amount), 0),
        [filteredPurchases]
    );
    const purchasesPage = paginateOrders(filteredPurchases, pPage, PAGE_SIZE);
    const hasPurchaseFilters = pSearch || pFrom || pTo || pSupplier || pStatus || pPayment;
    const resetPurchaseFilters = () => {
        setPSearch(''); setPFrom(''); setPTo(''); setPSupplier(undefined); setPStatus(undefined); setPPayment(undefined); setPPage(1);
    };

    const exportPurchasesCsv = () => {
        const rows = [['PO No', 'Purchase Date', 'Supplier', 'Supplier Ref', 'Status', 'Payment', 'Payment Method', 'Item', 'Qty', 'Unit Cost', 'Line Total', 'Shipping', 'Discount', 'PO Total']];
        filteredPurchases.forEach(p => {
            p.items.forEach((l, idx) => rows.push([
                p.purchase_no, p.purchase_date, p.supplier_name || '', p.reference_no || '', p.status, p.payment_status, p.payment_method || '',
                l.item_name, qty(l.quantity), num(l.unit_cost).toFixed(2), num(l.line_total).toFixed(2),
                idx === 0 ? num(p.shipping_fee).toFixed(2) : '', idx === 0 ? num(p.discount).toFixed(2) : '', idx === 0 ? num(p.total_amount).toFixed(2) : '',
            ]));
        });
        downloadCsv(`purchases-${todayStr()}.csv`, rows);
    };

    // ── New purchase ─────────────────────────────────────────────────────────
    const [purchaseForm, setPurchaseForm] = useState(null);
    const [purchaseSaving, setPurchaseSaving] = useState(false);
    const [purchaseError, setPurchaseError] = useState('');

    const openNewPurchase = (prefillItems = []) => {
        const form = emptyPurchaseForm();
        if (prefillItems.length) form.lines = prefillItems.map(i => newLine(i));
        setPurchaseForm(form);
        setPurchaseError('');
    };
    const closePurchase = () => { setPurchaseForm(null); setPurchaseError(''); };
    const setPF = (patch) => { setPurchaseForm(f => ({ ...f, ...patch })); setPurchaseError(''); };
    const setLine = (key, patch) => setPurchaseForm(f => ({ ...f, lines: f.lines.map(l => (l.key === key ? { ...l, ...patch } : l)) }));

    const purchaseSubtotal = purchaseForm
        ? purchaseForm.lines.reduce((s, l) => s + num(l.quantity) * num(l.unit_cost), 0)
        : 0;
    const purchaseTotal = purchaseSubtotal + num(purchaseForm?.shipping_fee) - num(purchaseForm?.discount);

    const handleSavePurchase = async () => {
        const f = purchaseForm;
        const lines = f.lines.filter(l => l.item_id);
        if (!f.purchase_date) return setPurchaseError('Purchase date is required');
        if (lines.length === 0) return setPurchaseError('Add at least one item');
        if (lines.some(l => !(num(l.quantity) > 0))) return setPurchaseError('Every item needs a quantity above 0');
        if (lines.some(l => l.unit_cost === '' || num(l.unit_cost) < 0)) return setPurchaseError('Every item needs a unit cost');
        if (new Set(lines.map(l => l.item_id)).size !== lines.length) return setPurchaseError('The same item is listed twice — combine the quantities into one line');
        if (purchaseTotal < 0) return setPurchaseError('Discount cannot be more than the subtotal + shipping');

        setPurchaseSaving(true);
        try {
            const data = await api('/purchases', {
                method: 'POST',
                body: JSON.stringify({
                    supplier_name: f.supplier_name,
                    purchase_date: f.purchase_date,
                    reference_no: f.reference_no,
                    status: f.status,
                    shipping_fee: f.shipping_fee,
                    discount: f.discount,
                    payment_status: f.payment_status,
                    payment_method: f.payment_method,
                    notes: f.notes,
                    items: lines.map(l => ({ item_id: l.item_id, quantity: l.quantity, unit_cost: l.unit_cost })),
                }),
            });
            message.success(`${data.purchase_no} saved${f.status === 'received' ? ' — stock updated' : ''}`);
            closePurchase();
            refreshAfterChange();
        } catch (err) {
            setPurchaseError(err.message);
        } finally {
            setPurchaseSaving(false);
        }
    };

    // ── Purchase detail / edit ───────────────────────────────────────────────
    const [detail, setDetail] = useState(null);
    const [detailForm, setDetailForm] = useState(null);
    const [detailSaving, setDetailSaving] = useState(false);
    const [detailError, setDetailError] = useState('');

    const openDetail = (p) => {
        setDetail(p);
        setDetailForm({
            supplier_name: p.supplier_name || '',
            purchase_date: p.purchase_date,
            reference_no: p.reference_no || '',
            payment_status: p.payment_status,
            payment_method: p.payment_method || undefined,
            paid_date: p.paid_date || todayStr(),
            notes: p.notes || '',
        });
        setDetailError('');
    };
    const closeDetail = () => { setDetail(null); setDetailForm(null); };

    const handleSaveDetail = async () => {
        setDetailSaving(true);
        setDetailError('');
        try {
            await api(`/purchases/${detail.id}`, { method: 'PATCH', body: JSON.stringify(detailForm) });
            message.success('Purchase updated');
            closeDetail();
            refreshAfterChange();
        } catch (err) {
            setDetailError(err.message);
        } finally {
            setDetailSaving(false);
        }
    };

    const markPaid = async (p) => {
        try {
            await api(`/purchases/${p.id}`, { method: 'PATCH', body: JSON.stringify({ payment_status: 'paid', paid_date: todayStr() }) });
            message.success(`${p.purchase_no} marked as paid`);
            refreshAfterChange();
        } catch (err) {
            message.error(err.message);
        }
    };

    const receivePurchase = async (p) => {
        if (!window.confirm(`Mark ${p.purchase_no} as received? This adds its quantities to available stock.`)) return;
        try {
            await api(`/purchases/${p.id}/receive`, { method: 'POST', body: JSON.stringify({ received_date: todayStr() }) });
            message.success(`${p.purchase_no} received — stock updated`);
            refreshAfterChange();
        } catch (err) {
            message.error(err.message);
        }
    };

    // ── Void ─────────────────────────────────────────────────────────────────
    const [voidTarget, setVoidTarget] = useState(null);
    const [voidReason, setVoidReason] = useState('');
    const [voidSaving, setVoidSaving] = useState(false);
    const [voidError, setVoidError] = useState('');

    const handleVoid = async () => {
        setVoidSaving(true);
        setVoidError('');
        try {
            await api(`/purchases/${voidTarget.id}/void`, { method: 'POST', body: JSON.stringify({ reason: voidReason }) });
            message.success(`${voidTarget.purchase_no} voided`);
            setVoidTarget(null);
            setVoidReason('');
            refreshAfterChange();
        } catch (err) {
            setVoidError(err.message);
        } finally {
            setVoidSaving(false);
        }
    };

    // ── Stock levels ─────────────────────────────────────────────────────────
    const [sSearch, setSSearch] = useState('');
    const [sLowOnly, setSLowOnly] = useState(false);
    const [sPage, setSPage] = useState(1);

    const stockRows = useMemo(() => {
        const q = sSearch.trim().toLowerCase();
        return purchasableItems
            .filter(i => !sLowOnly || (i.stock_on_hand !== null && num(i.stock_on_hand) <= lowStockThreshold))
            .filter(i => !q || i.name?.toLowerCase().includes(q) || i.sku?.toLowerCase().includes(q))
            .map(i => {
                const avgCost = num(i.total_qty) > 0 ? num(i.total_cost) / num(i.total_qty) : null;
                const rate = num(i.rate);
                return {
                    ...i,
                    avgCost,
                    margin: avgCost !== null && rate > 0 ? ((rate - avgCost) / rate) * 100 : null,
                    stockValue: avgCost !== null ? Math.max(0, num(i.stock_on_hand)) * avgCost : null,
                };
            });
    }, [purchasableItems, sSearch, sLowOnly, lowStockThreshold]);
    const stockPage = paginateOrders(stockRows, sPage, PAGE_SIZE);
    const totalStockValue = stockRows.reduce((s, r) => s + (r.stockValue || 0), 0);

    // ── Adjust stock ─────────────────────────────────────────────────────────
    const [adjustForm, setAdjustForm] = useState(null);
    const [adjustSaving, setAdjustSaving] = useState(false);
    const [adjustError, setAdjustError] = useState('');

    const openAdjust = (item) => {
        setAdjustForm({ item_id: item?.item_id, direction: 'remove', quantity: '1', reason: ADJUST_REASONS[0], note: '' });
        setAdjustError('');
    };
    const adjustItem = adjustForm ? itemsById[adjustForm.item_id] : null;
    const adjustChange = adjustForm ? (adjustForm.direction === 'add' ? 1 : -1) * num(adjustForm.quantity) : 0;

    const handleAdjust = async () => {
        if (!adjustForm.item_id) return setAdjustError('Choose an item');
        if (!(num(adjustForm.quantity) > 0)) return setAdjustError('Enter a quantity above 0');
        setAdjustSaving(true);
        setAdjustError('');
        try {
            await api('/adjustments', {
                method: 'POST',
                body: JSON.stringify({ item_id: adjustForm.item_id, quantity_change: adjustChange, reason: adjustForm.reason, note: adjustForm.note }),
            });
            message.success('Stock adjusted');
            setAdjustForm(null);
            refreshAfterChange();
        } catch (err) {
            setAdjustError(err.message);
        } finally {
            setAdjustSaving(false);
        }
    };

    // ── Item history ─────────────────────────────────────────────────────────
    const [historyItem, setHistoryItem] = useState(null);
    const [historyMovements, setHistoryMovements] = useState([]);
    const [historyLoading, setHistoryLoading] = useState(false);

    const openHistory = async (item) => {
        setHistoryItem(item);
        setHistoryMovements([]);
        setHistoryLoading(true);
        try {
            const data = await api(`/movements?item_id=${encodeURIComponent(item.item_id)}&limit=200`);
            setHistoryMovements(data.movements || []);
        } catch (err) {
            message.error(err.message);
        } finally {
            setHistoryLoading(false);
        }
    };
    const historyPurchaseLines = useMemo(() => {
        if (!historyItem) return [];
        return purchases.flatMap(p => p.items
            .filter(l => l.item_id === historyItem.item_id)
            .map(l => ({ ...l, purchase: p })));
    }, [historyItem, purchases]);

    // ── Movements ────────────────────────────────────────────────────────────
    const [mSearch, setMSearch] = useState('');
    const [mType, setMType] = useState();
    const [mPage, setMPage] = useState(1);
    const filteredMovements = useMemo(() => {
        const q = mSearch.trim().toLowerCase();
        return movements.filter(m => (!mType || m.movement_type === mType) &&
            (!q || [m.item_name, m.purchase_no, m.reason, m.note, m.created_by].some(v => v && String(v).toLowerCase().includes(q))));
    }, [movements, mSearch, mType]);
    const movementsPage = paginateOrders(filteredMovements, mPage, 50);

    // ── Suppliers ────────────────────────────────────────────────────────────
    const [supplierForm, setSupplierForm] = useState(null);
    const [supplierSaving, setSupplierSaving] = useState(false);
    const [supplierError, setSupplierError] = useState('');

    const handleSaveSupplier = async () => {
        if (!supplierForm.name.trim()) return setSupplierError('Supplier name is required');
        setSupplierSaving(true);
        setSupplierError('');
        try {
            const { id, name, contact_person, phone, email, notes } = supplierForm;
            await api(id ? `/suppliers/${id}` : '/suppliers', {
                method: id ? 'PUT' : 'POST',
                body: JSON.stringify({ name, contact_person, phone, email, notes }),
            });
            message.success(id ? 'Supplier updated' : 'Supplier added');
            setSupplierForm(null);
            refreshAll();
        } catch (err) {
            setSupplierError(err.message);
        } finally {
            setSupplierSaving(false);
        }
    };

    const handleDeleteSupplier = async (s) => {
        if (!window.confirm(`Delete supplier "${s.name}"? Their ${s.purchase_count} purchase(s) stay in the history without a supplier.`)) return;
        try {
            await api(`/suppliers/${s.id}`, { method: 'DELETE' });
            message.success('Supplier deleted');
            refreshAll();
        } catch (err) {
            message.error(err.message);
        }
    };

    const supplierOptions = suppliers.map(s => ({ value: s.id, label: s.name }));
    const itemOptions = purchasableItems.map(i => ({
        value: i.item_id,
        label: `${i.name}${i.sku ? ` (${i.sku})` : ''}`,
    }));

    const t = summary.totals || {};

    // ─────────────────────────────────────────────────────────────────────────
    return (
        <div className="tab-content inventory-tab">
            <div className="section-header">
                <h2>Inventory</h2>
                <div className="inv-header-actions">
                    <button className="btn-cancel" onClick={() => openAdjust(null)}>Adjust Stock</button>
                    <button className="btn-add" onClick={() => openNewPurchase()}>+ New Purchase</button>
                </div>
            </div>

            {/* KPI cards */}
            <div className="inv-kpis">
                <div className="inv-kpi">
                    <span className="inv-kpi-label">Spent this month</span>
                    <span className="inv-kpi-value">{money(t.month_spend)}</span>
                </div>
                <div className="inv-kpi">
                    <span className="inv-kpi-label">Spent this year</span>
                    <span className="inv-kpi-value">{money(t.year_spend)}</span>
                    <span className="inv-kpi-sub">All time {money(t.all_time_spend)}</span>
                </div>
                <button type="button" className="inv-kpi inv-kpi-clickable" onClick={() => { setSubTab('purchases'); resetPurchaseFilters(); setPPayment('unpaid'); }}>
                    <span className="inv-kpi-label">Owed to suppliers</span>
                    <span className="inv-kpi-value inv-kpi-alert">{money(t.unpaid_total)}</span>
                    <span className="inv-kpi-sub">{num(t.unpaid_count)} unpaid purchase{num(t.unpaid_count) === 1 ? '' : 's'}</span>
                </button>
                <button type="button" className="inv-kpi inv-kpi-clickable" onClick={() => { setSubTab('purchases'); resetPurchaseFilters(); setPStatus('ordered'); }}>
                    <span className="inv-kpi-label">Awaiting delivery</span>
                    <span className="inv-kpi-value">{num(t.pending_receive_count)}</span>
                    <span className="inv-kpi-sub">ordered, not yet in stock</span>
                </button>
                <button type="button" className="inv-kpi inv-kpi-clickable" onClick={() => { setSubTab('stock'); setSLowOnly(true); setSPage(1); }}>
                    <span className="inv-kpi-label">Low stock items</span>
                    <span className={`inv-kpi-value ${lowStockItems.length ? 'inv-kpi-alert' : ''}`}>{lowStockItems.length}</span>
                    <span className="inv-kpi-sub">at or below {lowStockThreshold}</span>
                </button>
            </div>

            {/* Monthly spend chart */}
            <div className="stats-chart-card inv-chart-card">
                <div className="stats-chart-title">Purchase spend — last 12 months (SGD)</div>
                <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                        <CartesianGrid vertical={false} stroke="#eee" />
                        <XAxis dataKey="label" tickLine={false} axisLine={{ stroke: '#ddd' }} tick={{ fontSize: 12, fill: '#666' }} />
                        <YAxis tickLine={false} axisLine={false} tick={{ fontSize: 12, fill: '#666' }} width={56} />
                        <Tooltip
                            cursor={{ fill: 'rgba(0,0,0,0.04)' }}
                            formatter={(value, _name, item) => [`${money(value)} (${item.payload.count} purchase${item.payload.count === 1 ? '' : 's'})`, 'Spend']}
                            labelFormatter={(label) => label}
                        />
                        <Bar dataKey="total" fill="#c41e3a" radius={[4, 4, 0, 0]} maxBarSize={32} />
                    </BarChart>
                </ResponsiveContainer>
            </div>

            {lowStockItems.length > 0 && subTab !== 'stock' && (
                <div className="inv-lowstock-banner">
                    <strong>{lowStockItems.length} item{lowStockItems.length === 1 ? '' : 's'} running low:</strong>{' '}
                    {lowStockItems.slice(0, 5).map(i => `${i.name} (${qty(i.stock_on_hand)})`).join(', ')}
                    {lowStockItems.length > 5 ? ` and ${lowStockItems.length - 5} more` : ''}
                    <button className="btn-save inv-small-btn" onClick={() => openNewPurchase(lowStockItems)}>Restock these</button>
                </div>
            )}

            {/* Sub-tabs */}
            <div className="inv-subtabs">
                {SUB_TABS.map(tab => (
                    <button
                        key={tab.key}
                        type="button"
                        className={`inv-subtab ${subTab === tab.key ? 'active' : ''}`}
                        onClick={() => setSubTab(tab.key)}
                    >
                        {tab.label}
                    </button>
                ))}
            </div>

            <ConfigProvider theme={THEME}>
                {loading && purchases.length === 0 && stockItems.length === 0 ? (
                    <div className="admin-loading">Loading...</div>
                ) : (
                    <>
                        {/* ===== PURCHASE HISTORY ===== */}
                        {subTab === 'purchases' && (
                            <>
                                <div className="inv-filters">
                                    <Input placeholder="Search PO, supplier, ref, item..." value={pSearch} allowClear
                                        onChange={e => { setPSearch(e.target.value); setPPage(1); }} style={{ width: 240 }} />
                                    <label className="inv-date-filter">From
                                        <input type="date" className="stats-date-input" value={pFrom} onChange={e => { setPFrom(e.target.value); setPPage(1); }} />
                                    </label>
                                    <label className="inv-date-filter">To
                                        <input type="date" className="stats-date-input" value={pTo} onChange={e => { setPTo(e.target.value); setPPage(1); }} />
                                    </label>
                                    <Select placeholder="Supplier" allowClear value={pSupplier} options={supplierOptions} showSearch optionFilterProp="label"
                                        onChange={v => { setPSupplier(v); setPPage(1); }} style={{ width: 170 }} />
                                    <Select placeholder="Status" allowClear value={pStatus} onChange={v => { setPStatus(v); setPPage(1); }} style={{ width: 130 }}
                                        options={[{ value: 'received', label: 'Received' }, { value: 'ordered', label: 'Ordered' }, { value: 'void', label: 'Void' }]} />
                                    <Select placeholder="Payment" allowClear value={pPayment} onChange={v => { setPPayment(v); setPPage(1); }} style={{ width: 130 }}
                                        options={[{ value: 'paid', label: 'Paid' }, { value: 'unpaid', label: 'Unpaid' }]} />
                                    {hasPurchaseFilters && <button className="btn-cancel inv-small-btn" onClick={resetPurchaseFilters}>Clear</button>}
                                    <button className="btn-cancel inv-small-btn" onClick={exportPurchasesCsv} disabled={filteredPurchases.length === 0}>Export CSV</button>
                                </div>
                                <div className="inv-filter-total">
                                    {filteredPurchases.length} purchase{filteredPurchases.length === 1 ? '' : 's'} · Total spend <strong>{money(filteredSpend)}</strong>
                                    <span className="inv-muted"> (void excluded)</span>
                                </div>
                                <div className="admin-table-wrapper">
                                    <table className="admin-table">
                                        <thead>
                                            <tr>
                                                <th>PO No</th>
                                                <th>Date</th>
                                                <th>Supplier</th>
                                                <th>Items</th>
                                                <th className="inv-num">Total</th>
                                                <th>Status</th>
                                                <th>Payment</th>
                                                <th>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {purchasesPage.items.map(p => (
                                                <tr key={p.id} className={p.status === 'void' ? 'inv-row-void' : ''}>
                                                    <td>
                                                        <button className="inv-link" onClick={() => openDetail(p)}>{p.purchase_no}</button>
                                                        {p.reference_no && <div className="inv-muted">Ref: {p.reference_no}</div>}
                                                    </td>
                                                    <td>{p.purchase_date}</td>
                                                    <td>{p.supplier_name || <span className="inv-muted">—</span>}</td>
                                                    <td className="inv-items-cell">
                                                        {p.items.slice(0, 2).map(l => `${l.item_name} ×${qty(l.quantity)}`).join(', ')}
                                                        {p.items.length > 2 && <span className="inv-muted"> +{p.items.length - 2} more</span>}
                                                    </td>
                                                    <td className="inv-num" style={{ fontWeight: 600 }}>{money(p.total_amount)}</td>
                                                    <td><Pill kind={p.status} /></td>
                                                    <td>
                                                        {p.status === 'void' ? <span className="inv-muted">—</span> : <Pill kind={p.payment_status} />}
                                                        {p.payment_method && p.status !== 'void' && <div className="inv-muted">{p.payment_method}</div>}
                                                    </td>
                                                    <td className="action-cell">
                                                        <button className="btn-cancel" onClick={() => openDetail(p)}>View</button>
                                                        {p.status === 'ordered' && <button className="btn-save" onClick={() => receivePurchase(p)}>Receive</button>}
                                                        {p.status !== 'void' && p.payment_status === 'unpaid' && <button className="btn-save" onClick={() => markPaid(p)}>Mark Paid</button>}
                                                        {p.status !== 'void' && <button className="btn-delete" onClick={() => { setVoidTarget(p); setVoidReason(''); setVoidError(''); }}>Void</button>}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                    {filteredPurchases.length === 0 ? (
                                        <div className="no-data">
                                            {hasPurchaseFilters ? 'No purchases match your filters.' : 'No purchases recorded yet. Click "+ New Purchase" to log your first stock purchase.'}
                                        </div>
                                    ) : (
                                        <Pagination paginated={purchasesPage} onPage={setPPage} label="purchases" />
                                    )}
                                </div>
                            </>
                        )}

                        {/* ===== STOCK LEVELS ===== */}
                        {subTab === 'stock' && (
                            <>
                                <div className="inv-filters">
                                    <Input placeholder="Search item or SKU..." value={sSearch} allowClear
                                        onChange={e => { setSSearch(e.target.value); setSPage(1); }} style={{ width: 240 }} />
                                    <label className="inv-checkbox">
                                        <input type="checkbox" checked={sLowOnly} onChange={e => { setSLowOnly(e.target.checked); setSPage(1); }} />
                                        Low stock only
                                    </label>
                                    <label className="inv-date-filter">Low stock at or below
                                        <input type="number" min="0" className="stats-date-input" style={{ width: 70 }} value={lowStockThreshold}
                                            onChange={e => setLowStockThreshold(Math.max(0, num(e.target.value)))} />
                                    </label>
                                </div>
                                <div className="inv-filter-total">
                                    Estimated stock value (on hand × average cost): <strong>{money(totalStockValue)}</strong>
                                </div>
                                <div className="admin-table-wrapper">
                                    <table className="admin-table">
                                        <thead>
                                            <tr>
                                                <th>Item</th>
                                                <th className="inv-num">Available</th>
                                                <th className="inv-num">Selling Rate</th>
                                                <th className="inv-num">Last Cost</th>
                                                <th className="inv-num">Avg Cost</th>
                                                <th className="inv-num">Margin</th>
                                                <th className="inv-num">Stock Value</th>
                                                <th className="inv-num">Total Bought</th>
                                                <th>Last Purchased</th>
                                                <th>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {stockPage.items.map(i => {
                                                const low = i.stock_on_hand !== null && num(i.stock_on_hand) <= lowStockThreshold;
                                                return (
                                                    <tr key={i.item_id}>
                                                        <td>
                                                            <button className="inv-link" onClick={() => openHistory(i)}>{i.name}</button>
                                                            {i.sku && <div className="inv-muted"><code>{i.sku}</code></div>}
                                                        </td>
                                                        <td className="inv-num">
                                                            <span className={`inv-stock ${low ? 'low' : ''}`}>
                                                                {i.stock_on_hand !== null ? qty(i.stock_on_hand) : '—'}
                                                            </span>
                                                        </td>
                                                        <td className="inv-num">{i.rate != null ? money(i.rate) : '—'}</td>
                                                        <td className="inv-num">{i.last_unit_cost != null ? money(i.last_unit_cost) : '—'}</td>
                                                        <td className="inv-num">{i.avgCost !== null ? money(i.avgCost) : '—'}</td>
                                                        <td className="inv-num">
                                                            {i.margin !== null
                                                                ? <span className={i.margin < 0 ? 'inv-negative' : ''}>{i.margin.toFixed(0)}%</span>
                                                                : '—'}
                                                        </td>
                                                        <td className="inv-num">{i.stockValue !== null ? money(i.stockValue) : '—'}</td>
                                                        <td className="inv-num">{i.total_qty != null ? qty(i.total_qty) : '—'}</td>
                                                        <td>{i.last_purchase_date || '—'}</td>
                                                        <td className="action-cell">
                                                            <button className="btn-save" onClick={() => openNewPurchase([i])}>Restock</button>
                                                            <button className="btn-cancel" onClick={() => openAdjust(i)}>Adjust</button>
                                                            <button className="btn-cancel" onClick={() => openHistory(i)}>History</button>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                    {stockRows.length === 0 ? (
                                        <div className="no-data">
                                            {sSearch || sLowOnly ? 'No items match.' : 'No items yet. Add items in the Items tab first.'}
                                        </div>
                                    ) : (
                                        <Pagination paginated={stockPage} onPage={setSPage} label="items" />
                                    )}
                                </div>
                            </>
                        )}

                        {/* ===== STOCK MOVEMENTS ===== */}
                        {subTab === 'movements' && (
                            <>
                                <div className="inv-filters">
                                    <Input placeholder="Search item, PO, reason..." value={mSearch} allowClear
                                        onChange={e => { setMSearch(e.target.value); setMPage(1); }} style={{ width: 240 }} />
                                    <Select placeholder="Type" allowClear value={mType} onChange={v => { setMType(v); setMPage(1); }} style={{ width: 160 }}
                                        options={Object.entries(MOVEMENT_LABELS).map(([value, m]) => ({ value, label: m.label }))} />
                                </div>
                                <MovementsTable rows={movementsPage.items} />
                                {filteredMovements.length === 0 ? (
                                    <div className="no-data">No stock movements yet. Every purchase, void, adjustment and string order will show up here.</div>
                                ) : (
                                    <Pagination paginated={movementsPage} onPage={setMPage} label="movements" />
                                )}
                            </>
                        )}

                        {/* ===== SUPPLIERS ===== */}
                        {subTab === 'suppliers' && (
                            <>
                                <div className="inv-filters">
                                    <button className="btn-add" onClick={() => { setSupplierForm({ ...EMPTY_SUPPLIER }); setSupplierError(''); }}>+ Add Supplier</button>
                                </div>
                                <div className="admin-table-wrapper">
                                    <table className="admin-table">
                                        <thead>
                                            <tr>
                                                <th>Supplier</th>
                                                <th>Contact</th>
                                                <th className="inv-num">Purchases</th>
                                                <th className="inv-num">Total Spend</th>
                                                <th className="inv-num">Unpaid</th>
                                                <th>Last Purchase</th>
                                                <th>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {suppliers.map(s => (
                                                <tr key={s.id}>
                                                    <td style={{ fontWeight: 600 }}>
                                                        {s.name}
                                                        {s.notes && <div className="inv-muted" style={{ fontWeight: 400 }}>{s.notes}</div>}
                                                    </td>
                                                    <td>
                                                        {[s.contact_person, s.phone, s.email].filter(Boolean).join(' · ') || <span className="inv-muted">—</span>}
                                                    </td>
                                                    <td className="inv-num">{num(s.purchase_count)}</td>
                                                    <td className="inv-num">{money(s.total_spend)}</td>
                                                    <td className="inv-num">
                                                        <span className={num(s.unpaid_total) > 0 ? 'inv-negative' : ''}>{money(s.unpaid_total)}</span>
                                                    </td>
                                                    <td>{s.last_purchase_date || '—'}</td>
                                                    <td className="action-cell">
                                                        <button className="btn-cancel" onClick={() => { setSubTab('purchases'); resetPurchaseFilters(); setPSupplier(s.id); }}>Purchases</button>
                                                        <button className="btn-cancel" onClick={() => { setSupplierForm({ ...EMPTY_SUPPLIER, ...s, contact_person: s.contact_person || '', phone: s.phone || '', email: s.email || '', notes: s.notes || '' }); setSupplierError(''); }}>Edit</button>
                                                        <button className="btn-delete" onClick={() => handleDeleteSupplier(s)}>Delete</button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                    {suppliers.length === 0 && (
                                        <div className="no-data">No suppliers yet. Add one here, or type a new name when recording a purchase.</div>
                                    )}
                                </div>
                            </>
                        )}
                    </>
                )}
            </ConfigProvider>

            {/* ===== NEW PURCHASE MODAL ===== */}
            {purchaseForm && (
                <Modal title="New Purchase" onClose={closePurchase} width={820}>
                    <Form layout="vertical" requiredMark="">
                        <div className="inv-form-row">
                            <Form.Item label="Supplier" style={{ flex: 2 }}>
                                <AutoComplete
                                    size="large"
                                    value={purchaseForm.supplier_name}
                                    options={suppliers.map(s => ({ value: s.name }))}
                                    filterOption={(input, opt) => opt.value.toLowerCase().includes(input.toLowerCase())}
                                    onChange={v => setPF({ supplier_name: v })}
                                    placeholder="Choose or type a new supplier"
                                />
                            </Form.Item>
                            <Form.Item label="Purchase Date" required style={{ flex: 1 }}>
                                <Input size="large" type="date" value={purchaseForm.purchase_date} onChange={e => setPF({ purchase_date: e.target.value })} />
                            </Form.Item>
                            <Form.Item label="Supplier Invoice / Ref" style={{ flex: 1 }}>
                                <Input size="large" value={purchaseForm.reference_no} onChange={e => setPF({ reference_no: e.target.value })} />
                            </Form.Item>
                        </div>

                        <Form.Item label="Delivery">
                            <Select
                                size="large"
                                value={purchaseForm.status}
                                onChange={v => setPF({ status: v })}
                                options={[
                                    { value: 'received', label: 'Received — add to available stock now' },
                                    { value: 'ordered', label: 'Ordered — not arrived yet (receive later)' },
                                ]}
                            />
                        </Form.Item>

                        <div className="inv-lines">
                            <div className="inv-line inv-line-head">
                                <span>Item</span><span>Qty</span><span>Unit Cost (SGD)</span><span className="inv-num">Line Total</span><span />
                            </div>
                            {purchaseForm.lines.map(l => {
                                const item = itemsById[l.item_id];
                                const lineTotal = num(l.quantity) * num(l.unit_cost);
                                return (
                                    <div key={l.key} className="inv-line">
                                        <div>
                                            <Select
                                                showSearch
                                                optionFilterProp="label"
                                                placeholder="Select item"
                                                value={l.item_id}
                                                options={itemOptions}
                                                style={{ width: '100%' }}
                                                onChange={v => {
                                                    const it = itemsById[v];
                                                    setLine(l.key, {
                                                        item_id: v,
                                                        unit_cost: l.unit_cost === '' && it?.last_unit_cost != null ? num(it.last_unit_cost).toFixed(2) : l.unit_cost,
                                                    });
                                                    setPurchaseError('');
                                                }}
                                            />
                                            {item && (
                                                <div className="inv-line-hint">
                                                    Available {item.stock_on_hand !== null ? qty(item.stock_on_hand) : '0'}
                                                    {purchaseForm.status === 'received' && num(l.quantity) > 0 && ` → ${qty(num(item.stock_on_hand) + num(l.quantity))}`}
                                                    {item.last_unit_cost != null && ` · last cost ${money(item.last_unit_cost)}`}
                                                </div>
                                            )}
                                        </div>
                                        <Input type="number" min="0" step="1" value={l.quantity}
                                            onChange={e => { setLine(l.key, { quantity: e.target.value }); setPurchaseError(''); }} />
                                        <Input type="number" min="0" step="0.01" value={l.unit_cost} placeholder="0.00"
                                            onChange={e => { setLine(l.key, { unit_cost: e.target.value }); setPurchaseError(''); }} />
                                        <span className="inv-num inv-line-total">{money(lineTotal)}</span>
                                        <button type="button" className="inv-remove" title="Remove line"
                                            disabled={purchaseForm.lines.length === 1}
                                            onClick={() => setPurchaseForm(f => ({ ...f, lines: f.lines.filter(x => x.key !== l.key) }))}>×</button>
                                    </div>
                                );
                            })}
                            <button type="button" className="btn-cancel inv-small-btn" onClick={() => setPurchaseForm(f => ({ ...f, lines: [...f.lines, newLine()] }))}>
                                + Add item
                            </button>
                        </div>

                        <div className="inv-form-row" style={{ marginTop: 16 }}>
                            <div style={{ flex: 1 }}>
                                <div className="inv-form-row">
                                    <Form.Item label="Payment" style={{ flex: 1 }}>
                                        <Select size="large" value={purchaseForm.payment_status} onChange={v => setPF({ payment_status: v })}
                                            options={[{ value: 'unpaid', label: 'Unpaid' }, { value: 'paid', label: 'Paid' }]} />
                                    </Form.Item>
                                    <Form.Item label="Method" style={{ flex: 1 }}>
                                        <Select size="large" allowClear placeholder="Optional" value={purchaseForm.payment_method}
                                            onChange={v => setPF({ payment_method: v })}
                                            options={PAYMENT_METHODS.map(m => ({ value: m, label: m }))} />
                                    </Form.Item>
                                </div>
                                <Form.Item label="Notes">
                                    <Input.TextArea rows={2} value={purchaseForm.notes} onChange={e => setPF({ notes: e.target.value })} />
                                </Form.Item>
                            </div>
                            <div className="inv-totals">
                                <div><span>Subtotal</span><span>{money(purchaseSubtotal)}</span></div>
                                <div>
                                    <span>Shipping</span>
                                    <Input type="number" min="0" step="0.01" placeholder="0.00" value={purchaseForm.shipping_fee}
                                        onChange={e => setPF({ shipping_fee: e.target.value })} style={{ width: 110 }} />
                                </div>
                                <div>
                                    <span>Discount</span>
                                    <Input type="number" min="0" step="0.01" placeholder="0.00" value={purchaseForm.discount}
                                        onChange={e => setPF({ discount: e.target.value })} style={{ width: 110 }} />
                                </div>
                                <div className="inv-grand-total"><span>Total</span><span>{money(purchaseTotal)}</span></div>
                            </div>
                        </div>

                        <ErrorBox>{purchaseError}</ErrorBox>
                        <div className="form-actions">
                            <button type="button" className="btn-cancel" onClick={closePurchase}>Cancel</button>
                            <Button type="primary" size="large" loading={purchaseSaving} onClick={handleSavePurchase}>
                                Save Purchase
                            </Button>
                        </div>
                    </Form>
                </Modal>
            )}

            {/* ===== PURCHASE DETAIL MODAL ===== */}
            {detail && detailForm && (
                <Modal title={<>{detail.purchase_no} <Pill kind={detail.status} /></>} onClose={closeDetail} width={720}>
                    <div className="inv-detail-meta">
                        <span>Recorded by {detail.created_by || '—'} on {formatDateTime(detail.created_at)}</span>
                        {detail.received_date && <span>Received {detail.received_date}</span>}
                        {detail.paid_date && <span>Paid {detail.paid_date}</span>}
                        {detail.void_reason && <span className="inv-negative">Void reason: {detail.void_reason}</span>}
                    </div>
                    <table className="admin-table inv-detail-table">
                        <thead>
                            <tr><th>Item</th><th className="inv-num">Qty</th><th className="inv-num">Unit Cost</th><th className="inv-num">Line Total</th></tr>
                        </thead>
                        <tbody>
                            {detail.items.map(l => (
                                <tr key={l.id}>
                                    <td>{l.item_name}</td>
                                    <td className="inv-num">{qty(l.quantity)}</td>
                                    <td className="inv-num">{money(l.unit_cost)}</td>
                                    <td className="inv-num">{money(l.line_total)}</td>
                                </tr>
                            ))}
                            <tr><td colSpan={3} className="inv-num">Subtotal</td><td className="inv-num">{money(detail.subtotal)}</td></tr>
                            {num(detail.shipping_fee) > 0 && <tr><td colSpan={3} className="inv-num">Shipping</td><td className="inv-num">{money(detail.shipping_fee)}</td></tr>}
                            {num(detail.discount) > 0 && <tr><td colSpan={3} className="inv-num">Discount</td><td className="inv-num">−{money(detail.discount)}</td></tr>}
                            <tr><td colSpan={3} className="inv-num"><strong>Total</strong></td><td className="inv-num"><strong>{money(detail.total_amount)}</strong></td></tr>
                        </tbody>
                    </table>

                    <Form layout="vertical" requiredMark="" style={{ marginTop: 16 }}>
                        <div className="inv-form-row">
                            <Form.Item label="Supplier" style={{ flex: 2 }}>
                                <AutoComplete size="large" value={detailForm.supplier_name}
                                    options={suppliers.map(s => ({ value: s.name }))}
                                    filterOption={(input, opt) => opt.value.toLowerCase().includes(input.toLowerCase())}
                                    onChange={v => setDetailForm(f => ({ ...f, supplier_name: v }))} />
                            </Form.Item>
                            <Form.Item label="Purchase Date" style={{ flex: 1 }}>
                                <Input size="large" type="date" value={detailForm.purchase_date}
                                    onChange={e => setDetailForm(f => ({ ...f, purchase_date: e.target.value }))} />
                            </Form.Item>
                            <Form.Item label="Supplier Invoice / Ref" style={{ flex: 1 }}>
                                <Input size="large" value={detailForm.reference_no}
                                    onChange={e => setDetailForm(f => ({ ...f, reference_no: e.target.value }))} />
                            </Form.Item>
                        </div>
                        {detail.status !== 'void' && (
                            <div className="inv-form-row">
                                <Form.Item label="Payment" style={{ flex: 1 }}>
                                    <Select size="large" value={detailForm.payment_status}
                                        onChange={v => setDetailForm(f => ({ ...f, payment_status: v }))}
                                        options={[{ value: 'unpaid', label: 'Unpaid' }, { value: 'paid', label: 'Paid' }]} />
                                </Form.Item>
                                <Form.Item label="Method" style={{ flex: 1 }}>
                                    <Select size="large" allowClear placeholder="Optional" value={detailForm.payment_method}
                                        onChange={v => setDetailForm(f => ({ ...f, payment_method: v ?? null }))}
                                        options={PAYMENT_METHODS.map(m => ({ value: m, label: m }))} />
                                </Form.Item>
                                {detailForm.payment_status === 'paid' && (
                                    <Form.Item label="Paid Date" style={{ flex: 1 }}>
                                        <Input size="large" type="date" value={detailForm.paid_date}
                                            onChange={e => setDetailForm(f => ({ ...f, paid_date: e.target.value }))} />
                                    </Form.Item>
                                )}
                            </div>
                        )}
                        <Form.Item label="Notes">
                            <Input.TextArea rows={2} value={detailForm.notes} onChange={e => setDetailForm(f => ({ ...f, notes: e.target.value }))} />
                        </Form.Item>
                        <div className="inv-muted" style={{ marginBottom: 12 }}>
                            Item quantities and costs can't be edited after saving, so stock history stays accurate. To correct them, void this purchase and record a new one.
                        </div>
                        <ErrorBox>{detailError}</ErrorBox>
                        <div className="form-actions">
                            <button type="button" className="btn-cancel" onClick={closeDetail}>Close</button>
                            <Button type="primary" size="large" loading={detailSaving} onClick={handleSaveDetail}>Save Changes</Button>
                        </div>
                    </Form>
                </Modal>
            )}

            {/* ===== VOID MODAL ===== */}
            {voidTarget && (
                <Modal title={`Void ${voidTarget.purchase_no}`} onClose={() => setVoidTarget(null)} width={440}>
                    <p style={{ marginBottom: 12 }}>
                        {voidTarget.status === 'received'
                            ? 'This will remove the purchased quantities from available stock:'
                            : 'This purchase was never received, so stock will not change.'}
                    </p>
                    {voidTarget.status === 'received' && (
                        <ul className="inv-void-list">
                            {voidTarget.items.map(l => {
                                const it = itemsById[l.item_id];
                                return (
                                    <li key={l.id}>
                                        {l.item_name}: −{qty(l.quantity)}
                                        {it && ` (${qty(it.stock_on_hand)} → ${qty(num(it.stock_on_hand) - num(l.quantity))})`}
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                    <Form layout="vertical">
                        <Form.Item label="Reason">
                            <Input size="large" autoFocus value={voidReason} placeholder="e.g. Entered by mistake, returned to supplier"
                                onChange={e => setVoidReason(e.target.value)} />
                        </Form.Item>
                        <ErrorBox>{voidError}</ErrorBox>
                        <div className="form-actions">
                            <button type="button" className="btn-cancel" onClick={() => setVoidTarget(null)}>Cancel</button>
                            <Button danger type="primary" size="large" loading={voidSaving} onClick={handleVoid}>Void Purchase</Button>
                        </div>
                    </Form>
                </Modal>
            )}

            {/* ===== ADJUST STOCK MODAL ===== */}
            {adjustForm && (
                <Modal title="Adjust Stock" onClose={() => setAdjustForm(null)} width={460}>
                    <Form layout="vertical" requiredMark="">
                        <Form.Item label="Item" required>
                            <Select size="large" showSearch optionFilterProp="label" placeholder="Select item"
                                value={adjustForm.item_id} options={itemOptions}
                                onChange={v => { setAdjustForm(f => ({ ...f, item_id: v })); setAdjustError(''); }} />
                        </Form.Item>
                        <div className="inv-form-row">
                            <Form.Item label="Change" style={{ flex: 1 }}>
                                <Select size="large" value={adjustForm.direction} onChange={v => setAdjustForm(f => ({ ...f, direction: v }))}
                                    options={[{ value: 'remove', label: 'Remove (−)' }, { value: 'add', label: 'Add (+)' }]} />
                            </Form.Item>
                            <Form.Item label="Quantity" required style={{ flex: 1 }}>
                                <Input size="large" type="number" min="0" step="1" value={adjustForm.quantity}
                                    onChange={e => { setAdjustForm(f => ({ ...f, quantity: e.target.value })); setAdjustError(''); }} />
                            </Form.Item>
                        </div>
                        <Form.Item label="Reason">
                            <Select size="large" value={adjustForm.reason} onChange={v => setAdjustForm(f => ({ ...f, reason: v }))}
                                options={ADJUST_REASONS.map(r => ({ value: r, label: r }))} />
                        </Form.Item>
                        <Form.Item label="Note">
                            <Input size="large" value={adjustForm.note} onChange={e => setAdjustForm(f => ({ ...f, note: e.target.value }))} />
                        </Form.Item>
                        {adjustItem && (
                            <div className="inv-adjust-preview">
                                Available: <strong>{qty(adjustItem.stock_on_hand)}</strong> → <strong>{qty(num(adjustItem.stock_on_hand) + adjustChange)}</strong>
                            </div>
                        )}
                        <ErrorBox>{adjustError}</ErrorBox>
                        <div className="form-actions">
                            <button type="button" className="btn-cancel" onClick={() => setAdjustForm(null)}>Cancel</button>
                            <Button type="primary" size="large" loading={adjustSaving} onClick={handleAdjust}>Save Adjustment</Button>
                        </div>
                    </Form>
                </Modal>
            )}

            {/* ===== ITEM HISTORY MODAL ===== */}
            {historyItem && (
                <Modal title={`${historyItem.name} — History`} onClose={() => setHistoryItem(null)} width={860}>
                    <div className="inv-detail-meta">
                        <span>Available now: <strong>{qty(itemsById[historyItem.item_id]?.stock_on_hand ?? historyItem.stock_on_hand)}</strong></span>
                        <span>Total bought: {qty(historyItem.total_qty)}</span>
                        <span>Total spent: {money(historyItem.total_cost)}</span>
                    </div>
                    <h4 className="inv-section-title">Purchases</h4>
                    {historyPurchaseLines.length === 0 ? (
                        <div className="no-data">No purchases recorded for this item.</div>
                    ) : (
                        <div className="admin-table-wrapper">
                            <table className="admin-table">
                                <thead>
                                    <tr><th>Date</th><th>PO No</th><th>Supplier</th><th className="inv-num">Qty</th><th className="inv-num">Unit Cost</th><th className="inv-num">Line Total</th><th>Status</th></tr>
                                </thead>
                                <tbody>
                                    {historyPurchaseLines.map(l => (
                                        <tr key={l.id} className={l.purchase.status === 'void' ? 'inv-row-void' : ''}>
                                            <td>{l.purchase.purchase_date}</td>
                                            <td>{l.purchase.purchase_no}</td>
                                            <td>{l.purchase.supplier_name || '—'}</td>
                                            <td className="inv-num">{qty(l.quantity)}</td>
                                            <td className="inv-num">{money(l.unit_cost)}</td>
                                            <td className="inv-num">{money(l.line_total)}</td>
                                            <td><Pill kind={l.purchase.status} /></td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                    <h4 className="inv-section-title">Stock movements</h4>
                    {historyLoading ? (
                        <div className="admin-loading">Loading...</div>
                    ) : historyMovements.length === 0 ? (
                        <div className="no-data">No stock movements recorded yet.</div>
                    ) : (
                        <MovementsTable rows={historyMovements} hideItem />
                    )}
                </Modal>
            )}

            {/* ===== SUPPLIER MODAL ===== */}
            {supplierForm && (
                <Modal title={supplierForm.id ? 'Edit Supplier' : 'Add Supplier'} onClose={() => setSupplierForm(null)} width={460}>
                    <Form layout="vertical" requiredMark="">
                        <Form.Item label="Supplier Name" required>
                            <Input size="large" autoFocus value={supplierForm.name}
                                onChange={e => { setSupplierForm(f => ({ ...f, name: e.target.value })); setSupplierError(''); }} />
                        </Form.Item>
                        <Form.Item label="Contact Person">
                            <Input size="large" value={supplierForm.contact_person} onChange={e => setSupplierForm(f => ({ ...f, contact_person: e.target.value }))} />
                        </Form.Item>
                        <div className="inv-form-row">
                            <Form.Item label="Phone" style={{ flex: 1 }}>
                                <Input size="large" value={supplierForm.phone} onChange={e => setSupplierForm(f => ({ ...f, phone: e.target.value }))} />
                            </Form.Item>
                            <Form.Item label="Email" style={{ flex: 1 }}>
                                <Input size="large" value={supplierForm.email} onChange={e => setSupplierForm(f => ({ ...f, email: e.target.value }))} />
                            </Form.Item>
                        </div>
                        <Form.Item label="Notes">
                            <Input.TextArea rows={2} value={supplierForm.notes} onChange={e => setSupplierForm(f => ({ ...f, notes: e.target.value }))} />
                        </Form.Item>
                        <ErrorBox>{supplierError}</ErrorBox>
                        <div className="form-actions">
                            <button type="button" className="btn-cancel" onClick={() => setSupplierForm(null)}>Cancel</button>
                            <Button type="primary" size="large" loading={supplierSaving} disabled={!supplierForm.name.trim()} onClick={handleSaveSupplier}>Save</Button>
                        </div>
                    </Form>
                </Modal>
            )}
        </div>
    );
}

function MovementsTable({ rows, hideItem = false }) {
    return (
        <div className="admin-table-wrapper">
            <table className="admin-table">
                <thead>
                    <tr>
                        <th>When</th>
                        {!hideItem && <th>Item</th>}
                        <th>Type</th>
                        <th className="inv-num">Change</th>
                        <th className="inv-num">Before → After</th>
                        <th>Reference</th>
                        <th>By</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map(m => {
                        const change = num(m.quantity_change);
                        return (
                            <tr key={m.id}>
                                <td style={{ whiteSpace: 'nowrap' }}>{formatDateTime(m.created_at)}</td>
                                {!hideItem && <td>{m.item_name || m.item_id}</td>}
                                <td><Pill kind={m.movement_type} map={MOVEMENT_LABELS} /></td>
                                <td className={`inv-num ${change < 0 ? 'inv-negative' : 'inv-positive'}`}>
                                    {change > 0 ? '+' : ''}{qty(change)}
                                </td>
                                <td className="inv-num">
                                    {m.stock_before !== null ? `${qty(m.stock_before)} → ${qty(m.stock_after)}` : '—'}
                                </td>
                                <td>
                                    {[m.purchase_no, m.reason, m.note].filter(Boolean).filter((v, i, a) => a.indexOf(v) === i).join(' · ') || '—'}
                                </td>
                                <td>{m.created_by || '—'}</td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}

export default InventoryTab;
